import { apiGet, apiPost } from '../shared/apiClient';

/**
 * Converte una stringa base64 URL-safe in un Uint8Array (necessario per applicationServerKey)
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Verifica se il browser supporta Service Worker e Push Notifications
 */
export function isPushSupported(): boolean {
  if (typeof window === 'undefined') return false;
  if (!window.isSecureContext) return false;
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Ritorna lo stato attuale del permesso di notifica: 'default' | 'granted' | 'denied' | 'unsupported' | 'insecure_context'
 */
export function getNotificationPermission(): NotificationPermission | 'unsupported' | 'insecure_context' {
  if (typeof window !== 'undefined' && !window.isSecureContext) return 'insecure_context';
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

/**
 * Registra il Service Worker dedicato al Push e sottoscrive il dispositivo
 */
export async function subscribeUserToPush(): Promise<{ ok: boolean; message?: string }> {
  if (typeof window !== 'undefined' && !window.isSecureContext) {
    return {
      ok: false,
      message: 'Le notifiche push richiedono HTTPS. Apri l\'app tramite https:// (es. https://IP:5174).',
    };
  }

  if (!isPushSupported()) {
    return { ok: false, message: 'Le notifiche push non sono supportate da questo browser o contesto' };
  }

  // 1. Richiedi permesso all'utente
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, message: 'Permesso per le notifiche non concesso' };
  }

  try {
    // 2. Riutilizza il Service Worker PWA già registrato (bug 4.2): non registriamo
    //    un secondo worker su scope '/', altrimenti vincerebbe su quello PWA e
    //    disattiverebbe la cache offline (o viceversa). Lo SW unificato (/sw.js)
    //    gestisce sia la precache offline sia le push notifications.
    let registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    }
    await navigator.serviceWorker.ready;

    // 3. Recupera la chiave pubblica VAPID dal backend
    const { data: keyData } = await apiGet<{ publicKey: string }>('/push/vapid-public-key');
    if (!keyData?.publicKey) {
      return { ok: false, message: 'Chiave VAPID non disponibile dal server' };
    }

    const applicationServerKey = urlBase64ToUint8Array(keyData.publicKey);

    // 4. Sottoscrizione PushManager (riusa esistente o ne crea una nuova)
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as unknown as BufferSource,
      });
    }

    const subJson = subscription.toJSON();
    if (!subJson.endpoint || !subJson.keys) {
      return { ok: false, message: 'Impossibile generare endpoint push valido' };
    }

    // 5. Invia SEMPRE la sottoscrizione al backend (anche se già esistente nel browser)
    //    per aggiornare il squadraId in caso di cambio account sullo stesso dispositivo
    await apiPost('/push/subscribe', {
      endpoint: subJson.endpoint,
      keys: subJson.keys,
    });

    return { ok: true };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Errore durante la registrazione push';
    console.error('[pushNotifications] Errore sottoscrizione:', err);
    return { ok: false, message: msg };
  }
}

/**
 * Disattiva la sottoscrizione push del dispositivo
 */
export async function unsubscribeUserFromPush(): Promise<{ ok: boolean; message?: string }> {
  if (!isPushSupported()) return { ok: true };

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await apiPost('/push/unsubscribe', { endpoint: subscription.endpoint }).catch(() => {});
      await subscription.unsubscribe();
    }
    return { ok: true };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Errore durante la disattivazione';
    return { ok: false, message: msg };
  }
}

/**
 * Invia una notifica di test al dispositivo per verificare suono, vibrazione e grafica
 */
export async function sendTestPushNotification(): Promise<{ ok: boolean; inviati?: number; totali?: number; error?: string }> {
  try {
    const { data } = await apiPost<{ ok: boolean; inviati: number; totali: number }>('/push/test', {});
    return data;
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Errore invio notifica test';
    return { ok: false, error: msg };
  }
}
